// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyCouponAction, TyCouponPreview } from "@/types";
import CouponBadge from "@/UI/components/CouponBadge";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    /** Which purchase the code is being applied to — the backend refuses codes not valid for it. */
    action: TyCouponAction;
    /** Only sent for subscription purchases; the branch add-on price is derived server-side. */
    period?: string;
    branches: number;
    applied: TyCouponPreview | null;
    onApply: (coupon: TyCouponPreview | null) => void;
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The code is only previewed here — nothing is reserved until the purchase itself is submitted, and
 * the backend re-runs every rule then. That keeps this purely about showing the client what they get.
 */
export default function CouponInput({ action, period, branches, applied, onApply, disabled }: Props) {
    const t = useTranslation();
    const [code, setCode] = useState("");

    const {
        data,
        loading,
        error,
        invoke: check,
        // `path` is re-read on every render, so it always carries whatever is in the field right now.
    } = useFetch<TyApiResponse<TyCouponPreview>>(`/subscriptions/coupon/${encodeURIComponent(code.trim() || "empty")}`, {}, false);

    useEffect(() => {
        if (data?.success) onApply(data.data);
    }, [data]);

    // The price a code is worth depends on the plan, so changing the plan drops the applied code
    // rather than leaving a stale discount on screen.
    useEffect(() => {
        if (applied) onApply(null);
    }, [action, period, branches]);

    const handleApply = () => {
        if (!code.trim() || loading) return;
        check({ query: { action, branches, ...(period ? { period } : {}) } });
    };

    const handleRemove = () => {
        setCode("");
        onApply(null);
    };

    if (applied) {
        return (
            <div className="coupon-field">
                <CouponBadge
                    code={applied.code}
                    discount={applied.discount}
                    savedAmount={applied.discountAmount}
                    onRemove={handleRemove}
                    disabled={disabled}
                />
            </div>
        );
    }

    return (
        <div className="coupon-field">
            <div className="coupon-row">
                <input
                    className="coupon-input"
                    dir="ltr"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        // The field lives inside the purchase form — Enter must not submit it.
                        e.preventDefault();
                        handleApply();
                    }}
                    placeholder={t.subscriptions.couponPlaceholder}
                    aria-label={t.subscriptions.couponLabel}
                    maxLength={24}
                    disabled={disabled || loading}
                />
                <button type="button" className="coupon-apply-btn" onClick={handleApply} disabled={disabled || loading || !code.trim()}>
                    {loading ? <span className="coupon-spinner" /> : t.subscriptions.couponApply}
                </button>
            </div>
            {error && <p className="coupon-error">{error}</p>}
        </div>
    );
}
